// ──────────────────────────────────────────────────────────────────────
// Revenue Overview widget — extracted from Dashboard.jsx and refactored to
// consume the Fretless design system. Card → WidgetHeader → 3×2 stat grid
// (Total Fees / Pending / Net Income on top, This Month / Tax Year / Unpaid
// Invoices below). Every colour, spacing, radius and border pulls from
// tokens.* — no hard-coded hex.
// ──────────────────────────────────────────────────────────────────────

import { Eye } from "lucide-react";
import { DragHandle } from "@/hooks/useDragHandle";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { Card, Band, Kicker, WidgetHeader } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

const { color, font, space, radius, border } = tokens;

// ───── Predicted income ─────────────────────────────────────────────
// Build the next `count` month buckets after a "YYYY-MM" anchor, reading each
// month's net (fees − expenses) straight from the revenue monthly_breakdown —
// which already includes future months. A month with no booked gigs reads £0
// (a flat forecast, not a gap). Anchoring on current_month keeps it aligned
// with the backend's user-timezone "this month" rather than the browser clock.
function predictedMonths(anchorYM, monthly, count = 6) {
  if (!anchorYM || !/^\d{4}-\d{2}$/.test(anchorYM)) return [];
  const [y, m] = anchorYM.split("-").map(Number);
  return Array.from({ length: count }, (_, i) => {
    // i + 1 → start at the first month AHEAD of the anchor. Day 1 + JS month
    // overflow handles year rollover (e.g. Dec → Jan) for free.
    const d = new Date(y, m - 1 + i + 1, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return {
      key,
      label: d.toLocaleDateString("en-GB", { month: "short" }),
      net: monthly?.[key]?.net || 0,
    };
  });
}

// ───── Stat tile (6-column variant) ─────────────────────────────────
// The DS StatTile is sized for big 3-col strips; this widget needs a
// denser 3×2 grid with optional sub-labels and click handling. Keeps the
// same kicker-then-value rhythm.
function RevenueTile({
  label,
  value,
  valueColor = color.text,
  sub,
  tint,
  onClick,
  testId,
}) {
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      data-testid={testId}
      style={{
        padding: `${space.md}px ${space.md}px`,
        background: tint || color.innerBg,
        border: border.inner,
        borderRadius: radius.lg,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        cursor: interactive ? "pointer" : "default",
        outline: "none",
        minHeight: 84,
        justifyContent: "center",
      }}
    >
      <Kicker size={11} color={color.textDim}>
        {label}
      </Kicker>
      <div
        style={{
          fontFamily: font.body,
          fontWeight: 700,
          fontSize: 22,
          lineHeight: 1,
          color: valueColor,
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontFamily: font.mono,
            fontSize: 11,
            color: color.textMuted,
            letterSpacing: "0.04em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function HideButton({ onHide }) {
  return (
    <button
      type="button"
      onClick={onHide}
      aria-label="Hide widget"
      title="Hide widget"
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.textMuted,
        border: "none",
        borderRadius: radius.md,
        cursor: "pointer",
      }}
    >
      <Eye className="h-4 w-4" />
    </button>
  );
}

// ───── Public component ────────────────────────────────────────────
export function RevenueOverview({
  revenueStats,
  unpaidInvoices = { count: 0, total: 0 },
  navigate,
  onHide,
}) {
  const totalFees = revenueStats?.total_fees || 0;
  const pending = revenueStats?.total_pending || 0;
  const netIncome = revenueStats?.net_income || 0;
  const thisMonthFees = revenueStats?.current_month?.fees || 0;
  // Compact UK tax-year label: "2026/2027" -> "26/27". Falls back to the
  // raw value if it doesn't match the four-digit-slash-four-digit shape.
  const taxYearRaw = revenueStats?.current_tax_year?.year;
  const taxYearShort = taxYearRaw && /^\d{4}\/\d{4}$/.test(taxYearRaw)
    ? `${taxYearRaw.slice(2, 4)}/${taxYearRaw.slice(7, 9)}`
    : taxYearRaw;
  const taxYearLabel = taxYearShort ? `Tax year ${taxYearShort}` : "Tax year";
  const taxYearFees = revenueStats?.current_tax_year?.fees || 0;

  // Predicted net income for the six months ahead, from the monthly breakdown.
  const predicted = predictedMonths(
    revenueStats?.current_month?.month,
    revenueStats?.monthly_breakdown,
  );

  const isDesktop = useIsDesktop(900);
  const compact = !isDesktop;
  // Mobile: 6 tiles in 2x3 grid instead of 3x2 — at ~370px viewport the
  // 3-col grid couldn't fit "TAX YEAR 2026/2027" / "UNPAID INVOICES"
  // kickers and the labels overlapped between adjacent tiles.
  const gridCols = compact
    ? "repeat(2, minmax(0, 1fr))"
    : "repeat(3, minmax(0, 1fr))";
  const headerPadding = compact ? `${space.md}px ${space.md}px` : undefined;
  const bodyPadding = compact
    ? `${space.md}px ${space.md}px ${space.md}px`
    : `${space.lg}px ${space.xl}px ${space.xl}px`;
  const gridGap = compact ? space.sm : space.md;

  return (
    <Card
      maxWidth="none"
      style={{ height: "100%" }}
      data-testid="revenue-overview-card"
    >
      <WidgetHeader
        title="Revenue overview"
        leading={<DragHandle />}
        padding={headerPadding}
        actions={<HideButton onHide={onHide} />}
      />
      <Band last padding={bodyPadding}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridCols,
            gap: gridGap,
          }}
        >
          <RevenueTile
            label="Total fees"
            value={`£${totalFees.toFixed(0)}`}
            valueColor={color.green}
            testId="stat-total-fees"
          />
          <RevenueTile
            label="Pending"
            value={`£${pending.toFixed(0)}`}
            valueColor={color.amber}
            testId="stat-pending"
          />
          <RevenueTile
            label="Net income"
            value={`£${netIncome.toFixed(0)}`}
            valueColor={netIncome >= 0 ? color.green : color.red}
            testId="stat-net-income"
          />
          <RevenueTile
            label="This month"
            value={`£${thisMonthFees.toFixed(0)}`}
            valueColor={color.orange}
            tint={color.orangeSoft}
            testId="stat-this-month"
          />
          <RevenueTile
            label={taxYearLabel}
            value={`£${taxYearFees.toFixed(0)}`}
            valueColor={color.green}
            tint={color.greenSoft}
            testId="stat-tax-year"
          />
          {unpaidInvoices.count === 0 ? (
            <RevenueTile
              label="Unpaid invoices"
              value={<span data-testid="unpaid-all-paid">All paid ✓</span>}
              valueColor={color.green}
              tint={color.greenSoft}
              testId="stat-unpaid-invoices"
            />
          ) : (
            <RevenueTile
              label="Unpaid invoices"
              value={`${unpaidInvoices.count} unpaid`}
              valueColor={color.red}
              sub={`£${unpaidInvoices.total.toFixed(0)} outstanding`}
              tint={color.redSoft}
              onClick={() => navigate("/invoices")}
              testId="stat-unpaid-invoices"
            />
          )}
        </div>

        {predicted.length > 0 && (
          <div style={{ marginTop: compact ? space.md : space.lg }}>
            <Kicker
              size={11}
              color={color.textDim}
              style={{ display: "block", marginBottom: gridGap, whiteSpace: "normal" }}
            >
              Predicted income · next 6 months
            </Kicker>
            {/* Always 3 columns: month labels ("JUL") are short enough to fit
                even at mobile width, and six tiles fill two clean rows of
                three with no orphan — which a 2- or 4-col grid would leave. */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: gridGap,
              }}
            >
              {predicted.map((p) => (
                <RevenueTile
                  key={p.key}
                  label={p.label}
                  // "-£150", not "£-150" — a negative month is likelier to
                  // appear now the forecast runs six months out.
                  value={`${p.net < 0 ? "-" : ""}£${Math.abs(p.net).toFixed(0)}`}
                  valueColor={p.net >= 0 ? color.green : color.red}
                  testId={`stat-predicted-${p.key}`}
                />
              ))}
            </div>
          </div>
        )}
      </Band>
    </Card>
  );
}

export default RevenueOverview;
